import * as THREE from 'three';

interface HeroVisualConfig {
  shape: 'dodecahedron' | 'icosahedron' | 'octahedron';
  size: number;
  rotationSpeed: {
    x: number;
    y: number;
  };
  mouseSensitivity: number;
  color: {
    wireframe: number;
    glow: number;
  };
  wireframeThickness: number;
  maxPixelRatio: number;
}

const HERO_VISUAL_CONFIG: HeroVisualConfig = {
  shape: 'dodecahedron',
  size: 2.5,
  rotationSpeed: {
    x: 0.002,
    y: 0.003
  },
  mouseSensitivity: 0.00005,
  color: {
    wireframe: 0xffffff,
    glow: 0xffffff
  },
  wireframeThickness: 0.02,
  maxPixelRatio: 2
};

function checkWebGLSupport(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
              (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
}

export class HeroVisual {
  private container: HTMLElement;

  private isAnimating = false;

  private scene!: THREE.Scene;

  private camera!: THREE.PerspectiveCamera;

  private renderer!: THREE.WebGLRenderer;

  private mesh!: THREE.LineSegments;

  private glowMesh!: THREE.Mesh;

  private rotationVelocity = { x: 0, y: 0 };

  private previousMousePosition = { x: 0, y: 0 };

  private lastMouseTime = 0;

  private resizeHandler: () => void;

  private mouseMoveHandler: (e: MouseEvent) => void;

  constructor(container: HTMLElement) {
    this.container = container;
    this.resizeHandler = this.onResize.bind(this);
    this.mouseMoveHandler = this.onMouseMove.bind(this);

    if (!checkWebGLSupport()) {
      console.warn('WebGL not supported, hero visual will not render');
      return;
    }

    try {
      this.init();
    } catch (error) {
      console.error('Hero visual initialization failed:', error);
    }
  }

  private init(): void {
    this.scene = new THREE.Scene();

    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 100);
    this.camera.position.z = 8;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, HERO_VISUAL_CONFIG.maxPixelRatio));
    this.container.appendChild(this.renderer.domElement);

    this.createGeometricShape();
    this.setupLights();

    this.onResize();
    window.addEventListener('resize', this.resizeHandler);
    this.container.addEventListener('mousemove', this.mouseMoveHandler);

    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.top = '0';
    this.renderer.domElement.style.left = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.pointerEvents = 'none';

    this.isAnimating = true;
    this.animate(0);
  }

  private createGeometricShape(): void {
    let geometry: THREE.BufferGeometry;

    switch (HERO_VISUAL_CONFIG.shape) {
      case 'icosahedron':
        geometry = new THREE.IcosahedronGeometry(HERO_VISUAL_CONFIG.size, 0);
        break;
      case 'octahedron':
        geometry = new THREE.OctahedronGeometry(HERO_VISUAL_CONFIG.size, 0);
        break;
      case 'dodecahedron':
      default:
        geometry = new THREE.DodecahedronGeometry(HERO_VISUAL_CONFIG.size, 0);
        break;
    }

    // Create wireframe
    const wireframeGeometry = new THREE.WireframeGeometry(geometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: HERO_VISUAL_CONFIG.color.wireframe,
      transparent: true,
      opacity: 0.8,
      linewidth: HERO_VISUAL_CONFIG.wireframeThickness
    });
    this.mesh = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
    this.scene.add(this.mesh);

    // Create inner glow mesh (hidden)
    const glowGeometry = geometry.clone();
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: HERO_VISUAL_CONFIG.color.glow,
      transparent: true,
      opacity: 0,
      side: THREE.BackSide
    });
    this.glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
    this.glowMesh.scale.set(0.95, 0.95, 0.95);
    this.scene.add(this.glowMesh);
  }

  private setupLights(): void {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(5, 5, 5);
    this.scene.add(directionalLight);
  }

  private animate(_currentTime: number): void {
    if (!this.isAnimating) return;

    requestAnimationFrame(this.animate.bind(this));

    // Apply friction to gradually slow down rotation velocity
    const friction = 0.98;
    this.rotationVelocity.x *= friction;
    this.rotationVelocity.y *= friction;

    // Apply rotation with base speed plus velocity
    if (this.mesh) {
      this.mesh.rotation.x += HERO_VISUAL_CONFIG.rotationSpeed.x + this.rotationVelocity.x;
      this.mesh.rotation.y += HERO_VISUAL_CONFIG.rotationSpeed.y + this.rotationVelocity.y;
    }

    if (this.glowMesh) {
      this.glowMesh.rotation.x = this.mesh.rotation.x;
      this.glowMesh.rotation.y = this.mesh.rotation.y;
    }

    this.renderer.render(this.scene, this.camera);
  }

  private onMouseMove(e: MouseEvent): void {
    const currentTime = Date.now();
    const timeDelta = currentTime - this.lastMouseTime;

    // Calculate mouse velocity relative to container
    if (timeDelta > 0) {
      const velocityX = (e.clientX - this.previousMousePosition.x) / timeDelta;
      const velocityY = (e.clientY - this.previousMousePosition.y) / timeDelta;

      // Apply mouse velocity as rotational impulse (scaled down)
      const impulseScale = 0.5;
      this.rotationVelocity.x += velocityY * impulseScale;
      this.rotationVelocity.y += velocityX * impulseScale;

      // Clamp maximum rotation velocity
      const maxVelocity = 0.1;
      this.rotationVelocity.x = Math.max(-maxVelocity, Math.min(maxVelocity, this.rotationVelocity.x));
      this.rotationVelocity.y = Math.max(-maxVelocity, Math.min(maxVelocity, this.rotationVelocity.y));
    }

    // Update previous mouse position and time
    this.previousMousePosition.x = e.clientX;
    this.previousMousePosition.y = e.clientY;
    this.lastMouseTime = currentTime;
  }

  private onResize(): void {
    this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
  }

  dispose(): void {
    this.isAnimating = false;

    window.removeEventListener('resize', this.resizeHandler);
    this.container.removeEventListener('mousemove', this.mouseMoveHandler);

    if (this.mesh) {
      this.mesh.geometry.dispose();
      const material = this.mesh.material;
      if (material instanceof THREE.Material) {
        material.dispose();
      }
    }

    if (this.glowMesh) {
      this.glowMesh.geometry.dispose();
      const material = this.glowMesh.material;
      if (material instanceof THREE.Material) {
        material.dispose();
      }
    }

    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement) {
        this.renderer.domElement.remove();
      }
    }
  }
}
